import { usePassportProvider } from "@/context/PassportProvider";
import { useUserBalance } from "@/hooks/useUserBalance";
import { notifyError } from "@/utils/monitoring";
import type { ErrorName } from "@/utils/monitoring";
import type { QuestState } from "@ui-kit/QuestList/types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { createContext, useContext } from "react";
import { useQuest } from "../QuestProvider";

type PointsMap = Record<string, number>;

interface PointsState {
  earnedPoints: number;
  earnedPointsMap: PointsMap;
  potentialPointsMap: PointsMap;
  isCalculating: boolean;
}

interface PointsProviderProps {
  children: React.ReactNode;
}

export const PointsProvider = ({ children }: PointsProviderProps) => {
  const { state: questGroupState } = useQuest((store) => store);

  const quests = useMemo(() => {
    return questGroupState.flatMap((group) => group.quests);
  }, [questGroupState]);

  const { walletAddress } = usePassportProvider();
  const { getERC20Balance, getERC721Balance, getERC1155Balance } =
    useUserBalance();

  const [pointsState, setPointsState] = useState<PointsState>({
    earnedPoints: 0,
    earnedPointsMap: {},
    potentialPointsMap: {},
    isCalculating: true,
  });

  const calculateQuestPoints = useCallback(
    async (quest: QuestState): Promise<number> => {
      if (!walletAddress || !quest.ruleId) return 0;

      let questPoints = quest.increase;

      if (quest.assetHoldingToken?.pointsMultiplier) {
        try {
          let balance = "0";

          switch (quest.assetHoldingType) {
            case "erc20": {
              const result = await getERC20Balance(
                walletAddress,
                quest.assetHoldingToken.collectionAddress,
              );
              balance = result.formatted;
              break;
            }

            case "erc721": {
              balance = await getERC721Balance(
                walletAddress,
                quest.assetHoldingToken.collectionAddress,
                quest.assetHoldingToken.tokenId
                  ? Number(quest.assetHoldingToken.tokenId)
                  : undefined,
              );
              break;
            }

            case "erc1155": {
              if (quest.assetHoldingToken.tokenId) {
                const balances = await getERC1155Balance(
                  walletAddress,
                  quest.assetHoldingToken.collectionAddress,
                  [Number(quest.assetHoldingToken.tokenId)],
                );
                balance = balances[0];
              }
              break;
            }
          }

          questPoints = Math.min(
            Math.floor(
              quest.assetHoldingToken.pointsMultiplier * Number(balance),
            ),
            quest.increase,
          );
        } catch (error) {
          notifyError(error as Error, "calculatePointsRPCError" as ErrorName, {
            questId: quest.ruleId,
            type: quest.assetHoldingType,
          });
          return 0;
        }
      }

      const isRecurringQuest =
        quest.recurring === true && quest.completionCount !== undefined;

      if (isRecurringQuest) {
        questPoints = quest.maxCompletionCount
          ? Math.min(
              quest.increase * (quest.completionCount as number),
              quest.increase * (quest.maxCompletionCount as number),
            )
          : quest.increase * (quest.completionCount as number);
      }

      return questPoints;
    },
    [walletAddress, getERC20Balance, getERC721Balance, getERC1155Balance],
  );

  const calculateAllPoints = useCallback(async () => {
    if (!walletAddress || !quests.length) {
      setPointsState({
        earnedPoints: 0,
        earnedPointsMap: {},
        potentialPointsMap: {},
        isCalculating: false,
      });
      return;
    }

    setPointsState((state) => ({ ...state, isCalculating: true }));

    try {
      const newEarnedPointsMap: PointsMap = {};
      const newPotentialPointsMap: PointsMap = {};
      let totalEarnedPoints = 0;

      const results = await Promise.allSettled(
        quests.map((quest) => calculateQuestPoints(quest)),
      );

      results.forEach((result, index) => {
        const quest = quests[index];
        if (!quest.ruleId) return;

        const points = result.status === "fulfilled" ? result.value : 0;
        newPotentialPointsMap[quest.ruleId] = points;

        const isActiveRecurringQuest =
          quest.recurring === true &&
          quest.completionCount !== undefined &&
          quest.completionCount > 0;

        if (quest.completed || isActiveRecurringQuest) {
          newEarnedPointsMap[quest.ruleId] = points;
          totalEarnedPoints += points;
        }
      });

      setPointsState({
        earnedPoints: totalEarnedPoints,
        earnedPointsMap: newEarnedPointsMap,
        potentialPointsMap: newPotentialPointsMap,
        isCalculating: false,
      });
    } catch (error) {
      notifyError(error as Error, "calculatePointsFatalError" as ErrorName, {
        walletAddress,
        questCount: quests.length,
      });
      setPointsState((state) => ({ ...state, isCalculating: false }));
    }
  }, [quests, walletAddress, calculateQuestPoints]);

  useEffect(() => {
    calculateAllPoints();
  }, [calculateAllPoints]);

  const pointsValue = useMemo(
    () => ({
      ...pointsState,
      recalculatePoints: calculateAllPoints,
    }),
    [pointsState, calculateAllPoints],
  );

  return (
    <PointsContext.Provider value={pointsValue}>
      {children}
    </PointsContext.Provider>
  );
};

const PointsContext = createContext<PointsState | undefined>(undefined);

export const usePoints = (): PointsState => {
  const context = useContext(PointsContext);
  if (!context) {
    throw new Error("usePoints must be used within a PointsProvider");
  }
  return context;
};
